import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const SECTION_FIELDS = [
  { key: 'from_my_desk', label: 'From My Desk' },
  { key: 'what_we_built', label: 'What We Built' },
  { key: 'ai_i_am_using', label: "AI I'm Using" },
  { key: 'business_lesson', label: 'Business Lesson' },
  { key: 'tech_i_am_testing', label: "Tech I'm Testing" },
  { key: 'opportunity_i_am_watching', label: "Opportunity I'm Watching" },
  { key: 'whats_coming_next', label: "What's Coming Next" },
  { key: 'founder_note', label: 'Founder Note' },
  { key: 'featured_company', label: 'Featured Company' },
  { key: 'recommended_resource', label: 'Recommended Resource' },
];

const STATUS_LABEL = { draft: 'Draft', scheduled: 'Scheduled', sent: 'Sent', archived: 'Archived' };
const EDITORIAL_TIMEZONE = 'America/New_York';

// --- Editorial timezone helpers (Eastern Time / America/New_York) ---
// Convert a "YYYY-MM-DDTHH:mm" wall-clock string (interpreted as ET) to a UTC ISO string.
// Handles DST correctly via Intl.DateTimeFormat introspection.
function etWallClockToUtcIso(localStr) {
  if (!localStr) return null;
  const [datePart, timePart = '00:00'] = localStr.split('T');
  const [Y, M, D] = datePart.split('-').map(Number);
  const [h, m] = timePart.split(':').map(Number);
  if ([Y, M, D, h, m].some((n) => Number.isNaN(n))) return null;
  const guessUtc = Date.UTC(Y, M - 1, D, h, m);
  // Represent that UTC instant in ET and read its wall-clock parts
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: EDITORIAL_TIMEZONE, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date(guessUtc)).map((p) => [p.type, p.value]));
  const asIfEt = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute);
  const offsetMs = asIfEt - guessUtc; // ET is behind UTC → negative
  return new Date(guessUtc - offsetMs).toISOString();
}

// Format any ISO UTC string as "Fri, Aug 14, 2026 · 9:00 AM ET"
function formatInEt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const dateStr = new Intl.DateTimeFormat('en-US', {
    timeZone: EDITORIAL_TIMEZONE,
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(d);
  const timeStr = new Intl.DateTimeFormat('en-US', {
    timeZone: EDITORIAL_TIMEZONE,
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  }).format(d);
  return `${dateStr} · ${timeStr}`;
}

// Return the current ET wall-clock as a "YYYY-MM-DDTHH:mm" string, offset by `minutesAhead`
function etNowMinutesAhead(minutesAhead = 0) {
  const now = new Date(Date.now() + minutesAhead * 60_000);
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: EDITORIAL_TIMEZONE, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(now).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${(+parts.hour % 24).toString().padStart(2, '0')}:${parts.minute}`;
}

export default function Editions() {
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminApi.get('/admin/brief-editions').then((r) => r.data);
      setList(d);
    } catch (_) {
      toast.error('Unable to load editions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createDraft = async () => {
    try {
      const d = await adminApi.post('/admin/brief-editions', { title: '' }).then((r) => r.data);
      const full = await adminApi.get(`/admin/brief-editions/${d.id}`).then((r) => r.data);
      setSelected(full);
      setEditorOpen(true);
      await load();
    } catch (e) {
      toast.error('Could not create draft.');
    }
  };

  const openEdition = async (id) => {
    const d = await adminApi.get(`/admin/brief-editions/${id}`).then((r) => r.data);
    setSelected(d);
    setEditorOpen(true);
  };

  return (
    <div data-testid="admin-editions-view">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Founder&rsquo;s Brief</div>
          <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
            Editions
          </h1>
          <p className="mt-3 text-[13px] text-neutral-500 max-w-lg">
            Weekly editions of The Founder&rsquo;s Brief. Drafts stay private. Test-send delivers to your admin inbox only. Scheduled sends are delivered by Resend at your chosen date &amp; time — editorial timezone is <strong>America/New_York</strong>.
          </p>
        </div>
        <button
          onClick={createDraft}
          data-testid="editions-new-draft"
          className="bg-neutral-900 text-white uppercase tracking-[0.22em] text-[10px] px-5 py-3 rounded-full hover:bg-[#1a1a1a]"
        >
          + New Draft
        </button>
      </header>

      <div className="bg-white/70 border border-neutral-200/60 rounded-xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50/70">
            <tr>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">#</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Title</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Status</th>
              <th className="text-left px-5 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200/60">
            {loading && (
              <tr><td colSpan={4} className="px-5 py-10 text-center uppercase tracking-[0.2em] text-[11px] text-neutral-500">Loading…</td></tr>
            )}
            {!loading && list.items.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-14 text-center text-neutral-500">No editions yet. Click &ldquo;+ New Draft&rdquo; to write the first one.</td></tr>
            )}
            {!loading && list.items.map((e) => (
              <tr
                key={e.id}
                data-testid="editions-row"
                onClick={() => openEdition(e.id)}
                className="cursor-pointer hover:bg-neutral-50"
              >
                <td className="px-5 py-3 text-neutral-500">{e.edition_number || '—'}</td>
                <td className="px-5 py-3 text-neutral-900">{e.title || '(untitled)'}</td>
                <td className="px-5 py-3">
                  <span className="uppercase tracking-[0.18em] text-[10px] text-[#C8A15A]">{STATUS_LABEL[e.status] || e.status}</span>
                </td>
                <td className="px-5 py-3 text-neutral-500 text-[11px]">
                  {e.updated_at ? new Date(e.updated_at).toLocaleString() : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editorOpen && selected && (
        <EditorDrawer
          edition={selected}
          onClose={() => { setEditorOpen(false); setSelected(null); load(); }}
          onPreview={setPreviewHtml}
        />
      )}
      {previewHtml && (
        <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />
      )}
    </div>
  );
}

function EditorDrawer({ edition, onClose, onPreview }) {
  const [form, setForm] = useState({
    title: edition.title || '',
    subject: edition.subject || '',
    preview_text: edition.preview_text || '',
    publish_date: edition.publish_date || '',
    body: edition.body || '',
    sections: edition.sections || {},
    status: edition.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendModal, setSendModal] = useState(false);
  const [current, setCurrent] = useState(edition);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (k, v) => setForm((f) => ({ ...f, sections: { ...(f.sections || {}), [k]: v } }));

  const reloadCurrent = async () => {
    const d = await adminApi.get(`/admin/brief-editions/${edition.id}`).then((r) => r.data);
    setCurrent(d);
    setForm((f) => ({ ...f, status: d.status || f.status }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/brief-editions/${edition.id}`, form);
      toast.success('Draft saved.');
      await reloadCurrent();
    } catch (e) {
      toast.error('Save failed.');
    } finally { setSaving(false); }
  };

  const preview = async () => {
    await saveDraft();
    const r = await adminApi.get(`/admin/brief-editions/${edition.id}/preview`).then((r) => r.data);
    onPreview(r.html);
  };

  const testSend = async () => {
    setTesting(true);
    try {
      await saveDraft();
      const r = await adminApi.post(`/admin/brief-editions/${edition.id}/test-send`).then((r) => r.data);
      toast.success(`Test email sent to ${r.sent_to}`);
    } catch (e) {
      toast.error('Test send failed.');
    } finally { setTesting(false); }
  };

  const openSendModal = async () => {
    await saveDraft();
    setSendModal(true);
  };

  const isLocked = current.status !== 'draft';

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30 backdrop-blur-sm" />
      <aside
        data-testid="editions-editor-drawer"
        className="fixed right-0 top-0 z-50 h-full w-full lg:w-[720px] bg-[#FAF8F5] border-l border-neutral-200/70 overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-[#FAF8F5]/95 backdrop-blur border-b border-neutral-200/60 px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="uppercase tracking-[0.24em] text-[10px] text-neutral-500 mb-1">
              Edition {current.edition_number || ''} · {STATUS_LABEL[current.status] || current.status}
            </div>
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-xl text-neutral-900">
              {form.title || '(untitled)'}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none px-2" aria-label="Close">×</button>
        </div>

        {current.status === 'scheduled' && (
          <ScheduledBanner edition={current} onChange={reloadCurrent} />
        )}
        {current.status === 'sent' && (
          <div className="mx-6 lg:mx-8 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-[13px] text-emerald-900">
            <div className="uppercase tracking-[0.2em] text-[10px] text-emerald-700 mb-1">Delivered</div>
            <div>Sent to {current.recipients_count || 0} subscribers on {current.sent_at ? new Date(current.sent_at).toLocaleString() : 'unknown time'}. This edition is locked from re-sending.</div>
          </div>
        )}

        <div className="px-6 lg:px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title" value={form.title} onChange={(v) => set('title', v)} disabled={isLocked} />
            <Field label="Subject Line" value={form.subject} onChange={(v) => set('subject', v)} disabled={isLocked} />
            <Field label="Preview Text" value={form.preview_text} onChange={(v) => set('preview_text', v)} disabled={isLocked} />
            <Field label="Publish Date" value={form.publish_date} onChange={(v) => set('publish_date', v)} placeholder="2026-08-01" disabled={isLocked} />
          </div>

          <div>
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Intro / Body (optional, HTML allowed)</label>
            <textarea
              rows={4}
              disabled={isLocked}
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900 disabled:bg-neutral-50 disabled:text-neutral-500"
              placeholder="Optional opening paragraph…"
            />
          </div>

          <div className="space-y-4">
            <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Sections (all optional)</div>
            {SECTION_FIELDS.map((s) => (
              <div key={s.key}>
                <label className="text-[11px] text-neutral-600 mb-1 block">{s.label}</label>
                <textarea
                  rows={3}
                  disabled={isLocked}
                  value={(form.sections || {})[s.key] || ''}
                  onChange={(e) => setSection(s.key, e.target.value)}
                  className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900 disabled:bg-neutral-50 disabled:text-neutral-500"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pt-4 border-t border-neutral-200/60">
            <button onClick={saveDraft} disabled={saving || isLocked} data-testid="editions-save" className="bg-neutral-900 text-white uppercase tracking-[0.2em] text-[10px] px-5 py-3 rounded-full hover:bg-[#1a1a1a] disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button onClick={preview} disabled={isLocked && current.status !== 'sent'} data-testid="editions-preview" className="uppercase tracking-[0.2em] text-[10px] px-5 py-3 border border-neutral-300 rounded-full hover:bg-neutral-100 disabled:opacity-50">
              Preview
            </button>
            <button onClick={testSend} disabled={testing || isLocked} data-testid="editions-test-send" className="uppercase tracking-[0.2em] text-[10px] px-5 py-3 border border-neutral-300 rounded-full hover:bg-neutral-100 disabled:opacity-50">
              {testing ? 'Sending…' : 'Send Test Email'}
            </button>
            {!isLocked && (
              <button
                onClick={openSendModal}
                data-testid="editions-open-send-modal"
                className="ml-auto uppercase tracking-[0.2em] text-[10px] px-5 py-3 rounded-full bg-[#C8A15A] text-white hover:bg-[#b8904a]"
              >
                Send to Full List →
              </button>
            )}
          </div>
          <p className="text-[11px] text-neutral-500 leading-relaxed">
            Live sends use Resend Broadcasts against your verified TOIA Wallace Newsletter audience. A 60-second cancellable grace period is built into every send.
          </p>
        </div>
      </aside>

      {sendModal && (
        <SendConfirmModal
          editionId={edition.id}
          onClose={() => setSendModal(false)}
          onSent={async () => { setSendModal(false); await reloadCurrent(); }}
        />
      )}
    </>
  );
}

function ScheduledBanner({ edition, onChange }) {
  const [remaining, setRemaining] = useState(0);
  const [canceling, setCanceling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const compute = () => {
      const target = new Date(edition.scheduled_send_at).getTime();
      const now = Date.now();
      setRemaining(Math.max(0, Math.floor((target - now) / 1000)));
    };
    compute();
    const iv = setInterval(compute, 1000);
    return () => clearInterval(iv);
  }, [edition.scheduled_send_at]);

  const cancel = async () => {
    if (!window.confirm('Cancel this scheduled broadcast? The edition returns to Draft.')) return;
    setCanceling(true);
    try {
      await adminApi.post(`/admin/brief-editions/${edition.id}/cancel-send`);
      toast.success('Send canceled — edition returned to Draft.');
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed.');
    } finally { setCanceling(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await adminApi.post(`/admin/brief-editions/${edition.id}/refresh-status`);
      await onChange();
    } catch (e) {
      toast.error('Refresh failed.');
    } finally { setRefreshing(false); }
  };

  const CANCEL_LOCKOUT_SECONDS = 60;
  const canCancel = remaining > CANCEL_LOCKOUT_SECONDS;
  const withinLockout = remaining > 0 && remaining <= CANCEL_LOCKOUT_SECONDS;
  const humanEt = formatInEt(edition.scheduled_send_at);

  // Human-friendly countdown formatter
  const fmtCountdown = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  };

  return (
    <div className="mx-6 lg:mx-8 mt-4 p-5 bg-[#C8A15A]/10 border border-[#C8A15A]/40 rounded-lg" data-testid="editions-scheduled-banner">
      <div className="uppercase tracking-[0.2em] text-[10px] text-[#8f6e2c] mb-1">Scheduled</div>
      <div className="text-[15px] font-medium text-neutral-900 mb-1" data-testid="editions-scheduled-when">
        Delivers <strong>{humanEt}</strong>
      </div>
      <div className="text-[12px] text-neutral-600 mb-3">
        To <strong>{edition.recipients_count}</strong> subscribers via Resend Broadcast · <span className="font-mono text-neutral-500">broadcast_id: {edition.broadcast_id}</span>
      </div>
      {canCancel && (
        <>
          <div className="text-[13px] text-neutral-800 mb-3">
            In <span className="tabular-nums font-medium">{fmtCountdown(remaining)}</span>. You can cancel or reschedule (draft → send again) up to <strong>{CANCEL_LOCKOUT_SECONDS}s</strong> before delivery.
          </div>
          <button
            onClick={cancel}
            disabled={canceling}
            data-testid="editions-cancel-send"
            className="uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 rounded-full bg-neutral-900 text-white hover:bg-[#1a1a1a] disabled:opacity-50"
          >
            {canceling ? 'Canceling…' : 'Cancel & Return to Draft'}
          </button>
        </>
      )}
      {withinLockout && (
        <div className="text-[13px] text-neutral-800">
          Cancel window closed — Resend is preparing to deliver in <span className="tabular-nums">{remaining}s</span>.
        </div>
      )}
      {remaining <= 0 && (
        <>
          <div className="text-[13px] text-neutral-700 mb-3">
            Scheduled time reached. Resend is delivering. Refresh to confirm.
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="editions-refresh-status"
            className="uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh Delivery Status'}
          </button>
        </>
      )}
    </div>
  );
}

function SendConfirmModal({ editionId, onClose, onSent }) {
  const [preflight, setPreflight] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [sending, setSending] = useState(false);
  // Local ET wall-clock string ("YYYY-MM-DDTHH:mm") for the <input type="datetime-local">.
  // Empty string = "send in 60 seconds" (the historical default).
  const [localSchedule, setLocalSchedule] = useState('');

  useEffect(() => {
    adminApi.get(`/admin/brief-editions/${editionId}/send-preflight`)
      .then((r) => setPreflight(r.data))
      .catch(() => setPreflight({ ready: false, reasons: ['Could not run preflight.'] }));
  }, [editionId]);

  // Validation of the datetime picker (client-side echo of the backend limits)
  const scheduleValidation = (() => {
    if (!localSchedule) return { ok: true, utcIso: null, humanEt: 'in ~60 seconds', kind: 'immediate' };
    const utcIso = etWallClockToUtcIso(localSchedule);
    if (!utcIso) return { ok: false, error: 'Invalid date.' };
    const target = new Date(utcIso).getTime();
    const now = Date.now();
    const minAt = preflight?.min_scheduled_at ? new Date(preflight.min_scheduled_at).getTime() : now + 60_000;
    const maxAt = preflight?.max_scheduled_at ? new Date(preflight.max_scheduled_at).getTime() : now + 30 * 86_400_000;
    if (target < minAt) return { ok: false, error: `Must be at least ${preflight?.grace_period_seconds || 60} seconds in the future.` };
    if (target > maxAt) return { ok: false, error: `Cannot be more than ${preflight?.max_schedule_days || 30} days in the future (Resend limit).` };
    return { ok: true, utcIso, humanEt: formatInEt(utcIso), kind: 'future' };
  })();

  const canSubmit = preflight?.ready && confirmText.trim().toUpperCase() === 'SEND' && !sending && scheduleValidation.ok;

  const submit = async () => {
    setSending(true);
    try {
      const payload = { confirmation: 'SEND' };
      if (scheduleValidation.utcIso) payload.scheduled_at = scheduleValidation.utcIso;
      await adminApi.post(`/admin/brief-editions/${editionId}/send`, payload);
      toast.success(scheduleValidation.kind === 'future' ? `Broadcast scheduled for ${scheduleValidation.humanEt}.` : 'Broadcast scheduled. 60-second cancel window active.');
      await onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed.');
      setSending(false);
    }
  };

  // Quick preset: this coming Friday 9am ET
  const nextFriday9amEt = (() => {
    const nowEt = new Date(new Intl.DateTimeFormat('en-US', { timeZone: EDITORIAL_TIMEZONE }).format(new Date()));
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: EDITORIAL_TIMEZONE, hour12: false, weekday: 'short',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date()).map((p) => [p.type, p.value]));
    const daysMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const todayDow = daysMap[parts.weekday] ?? 0;
    let add = (5 - todayDow + 7) % 7;
    if (add === 0) add = 7; // if today is Friday, use next Friday
    // Compute the ET calendar date `add` days from today
    const y = +parts.year, m = +parts.month, d = +parts.day;
    const fut = new Date(Date.UTC(y, m - 1, d + add, 13, 0, 0)); // 9am ET ~= 13:00-14:00 UTC; we only need the ET Y-M-D, time will be set below
    const futParts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: EDITORIAL_TIMEZONE, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(fut).map((p) => [p.type, p.value]));
    return `${futParts.year}-${futParts.month}-${futParts.day}T09:00`;
  })();

  return (
    <div className="fixed inset-0 z-[70] bg-neutral-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-[560px] w-full max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="editions-send-modal">
        <div className="px-6 py-5 border-b border-neutral-200/60">
          <div className="uppercase tracking-[0.22em] text-[10px] text-[#C8A15A] mb-2">Confirm Live Send</div>
          <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-2xl text-neutral-900">
            Send to the full Founder&rsquo;s List?
          </div>
        </div>
        <div className="px-6 py-6 space-y-5 text-[14px] text-neutral-800 leading-relaxed">
          {!preflight && <div className="uppercase tracking-[0.2em] text-[11px] text-neutral-500">Running preflight…</div>}
          {preflight && !preflight.ready && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-800 text-[13px]">
              <div className="font-semibold mb-2">This edition can&rsquo;t be sent yet:</div>
              <ul className="list-disc pl-5 space-y-1">
                {preflight.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {preflight && preflight.ready && (
            <>
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-2">
                <Row label="Edition" value={`#${preflight.edition_number || '?'} — ${preflight.title || '(untitled)'}`} />
                <Row label="Subject" value={preflight.subject} />
                <Row label="Active subscribers" value={<strong data-testid="preflight-active-count">{preflight.active_subscribers}</strong>} />
                <Row label="Editorial timezone" value={<strong>America/New_York (Eastern Time)</strong>} />
              </div>

              <div>
                <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Schedule date &amp; time (Eastern Time)</label>
                <input
                  type="datetime-local"
                  value={localSchedule}
                  onChange={(e) => setLocalSchedule(e.target.value)}
                  min={etNowMinutesAhead(2)}
                  data-testid="editions-schedule-input"
                  className="w-full bg-white border border-neutral-300 rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-neutral-900"
                />
                <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                  <div className="text-[12px] text-neutral-500">
                    {scheduleValidation.ok ? (
                      scheduleValidation.kind === 'immediate'
                        ? <>Leave blank to send <strong>in 60 seconds</strong> (cancellable). Or pick a future date/time.</>
                        : <>Delivers <strong>{scheduleValidation.humanEt}</strong> · <span className="text-neutral-400 tabular-nums">{new Date(scheduleValidation.utcIso).toISOString()}</span></>
                    ) : (
                      <span className="text-red-600">{scheduleValidation.error}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {localSchedule && (
                      <button type="button" onClick={() => setLocalSchedule('')} className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900" data-testid="editions-schedule-clear">Clear</button>
                    )}
                    <button type="button" onClick={() => setLocalSchedule(nextFriday9amEt)} className="text-[10px] uppercase tracking-[0.18em] text-[#8f6e2c] hover:text-[#C8A15A]" data-testid="editions-schedule-next-friday">
                      Next Friday · 9am ET
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-[13px] text-neutral-600">
                Once confirmed, Resend Broadcasts creates a broadcast and delivers it at the chosen time. You can cancel or reschedule up to <strong>{preflight.grace_period_seconds}s</strong> before delivery. Active-subscriber count and unsubscribe status are re-checked at the moment of scheduling; Resend re-checks the audience again at send time.
              </p>
              <div>
                <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Type <code>SEND</code> to confirm</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoFocus
                  data-testid="editions-confirm-input"
                  className="w-full bg-white border border-neutral-300 rounded-lg px-3 py-2 text-[14px] focus:outline-none focus:border-neutral-900 tracking-wider"
                  placeholder="SEND"
                />
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200/60 flex justify-end gap-2">
          <button onClick={onClose} className="uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-white">
            Back
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="editions-confirm-send"
            className="uppercase tracking-[0.2em] text-[10px] px-6 py-2.5 rounded-full bg-[#C8A15A] text-white hover:bg-[#b8904a] disabled:opacity-40"
          >
            {sending ? 'Scheduling…' : (scheduleValidation.kind === 'future' ? 'Confirm & Schedule' : 'Confirm & Send in 60s')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-[13px]">
      <div className="uppercase tracking-[0.18em] text-[10px] text-neutral-500 col-span-1 pt-0.5">{label}</div>
      <div className="col-span-2 text-neutral-900">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900"
      />
    </div>
  );
}

function PreviewModal({ html, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-neutral-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-[720px] w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-neutral-200/60 px-5 py-3 flex justify-between items-center">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">Preview</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-xl px-2" aria-label="Close">×</button>
        </div>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}
